// Shared helpers for the idle session timeout (see components/Authenticated.js).
// All timing is derived from wall-clock timestamps so background-tab timer throttling
// can delay a check but never stretch the countdown itself.

export const WARNING_SECONDS = 60; // grace period after `expiry` before the session is ended
export const ACTIVITY_THROTTLE_MS = 5000; // min gap between activity-driven expiry extensions

const REDIRECT_KEY = "redirectLocation";

export const SESSION_STATUS = {
    ACTIVE: "active",
    WARNING: "warning",
    EXPIRED: "expired",
};

/**
 * Work out where the session is relative to its stored expiry.
 * `expiry` is the moment the idle timeout elapsed (last activity + timeoutMinutes);
 * the warning runs from `expiry` until `expiry + WARNING_SECONDS`.
 */
export const getSessionTimeoutState = (expiry, timeoutMinutes, now = Date.now()) => {
    const expiryMs = new Date(expiry).getTime();
    if (!expiry || Number.isNaN(expiryMs)) {
        return { status: SESSION_STATUS.ACTIVE, deadline: null, inactiveMinutes: 0 };
    }
    const deadline = expiryMs + WARNING_SECONDS * 1000;
    const lastActivity = expiryMs - timeoutMinutes * 60 * 1000;
    const inactiveMinutes = Math.max(0, Math.floor((now - lastActivity) / 60000));

    let status = SESSION_STATUS.ACTIVE;
    if (now >= deadline) {
        status = SESSION_STATUS.EXPIRED;
    } else if (now >= expiryMs) {
        status = SESSION_STATUS.WARNING;
    }
    return { status, deadline, inactiveMinutes };
};

export const getSecondsLeft = (deadline, now = Date.now()) => Math.max(0, Math.ceil((deadline - now) / 1000));

// Only same-origin, in-app paths: must start with a single "/" (not "//" or "/\") and not be the auth pages.
export const isSafeRedirectPath = (path) =>
    typeof path === "string" && /^\/(?![/\\])/.test(path) && !/^\/(login|logout)(?:[/?#]|$)/i.test(path);

// sessionStorage is per-tab and survives the localStorage.clear() done on logout,
// so each tab returns to its own page after logging in again from that tab.
export const saveRedirectLocation = (path, username = null) => {
    if (!isSafeRedirectPath(path)) return;
    try {
        sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ path, username }));
    } catch (e) {
        // storage unavailable - fall back to the default landing page
    }
};

// Returns the saved path once (it is removed on read), only for the same user who was on it.
export const takeRedirectLocation = (username) => {
    let saved = null;
    try {
        saved = JSON.parse(sessionStorage.getItem(REDIRECT_KEY));
        sessionStorage.removeItem(REDIRECT_KEY);
    } catch (e) {
        return null;
    }
    if (!saved || !isSafeRedirectPath(saved.path)) return null;
    if (saved.username && saved.username !== username) return null;
    return saved.path;
};
